import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Notification from "../../components/Notification.js";
import { API_URL } from "../../config.js";
import { allowedToView } from "../../utils/PermissionUtils.js";
import { formatTimestamp, formatToDayMonthYear } from "../../utils/TextUtils.js";

function Aggregations() {
    const [aggregations, setAggregations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(null);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [selectedAggregation, setSelectedAggregation] = useState(null);
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filterDate, setFilterDate] = useState("");
    const [filterMonth, setFilterMonth] = useState("");
    const [filterRoute, setFilterRoute] = useState("");
    const [filterShift, setFilterShift] = useState("");
    
    // Negative variance totals only
    const [negativeVariance, setNegativeVariance] = useState({
        total: 0,
        count: 0,
        total_records: 0
    });
    const [totalsLoading, setTotalsLoading] = useState(false);

    const [routes, setRoutes] = useState([]);
    const perPage = 10;
    const token = localStorage.getItem("access_token");
    const userRole = localStorage.getItem("userRole");

    const shifts = [
        { value: "", label: "All Shifts" },
        { value: "Morning", label: "Morning" },
        { value: "Evening", label: "Evening" }
    ];

    const canEdit = (userRole) => {
        return (allowedToView(userRole, ['superadmin', 'aggregator']));
    };

    const isAdmin = userRole === 'superadmin' || userRole === 'admin';
    const navigate = useNavigate();

    // Fetch routes from API
    const fetchRoutes = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/collection-routes`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setRoutes(data || []);
            }
        } catch (error) {
            console.error("Error fetching routes:", error);
        }
    }, [token]);

    // Fetch aggregations
    const fetchAggregations = useCallback(async () => {
        try {
            setLoading(true);
            let url = `${API_URL}/route-aggregations?page=${currentPage}&limit=${perPage}`;

            if (searchTerm) {
                url += `&search=${searchTerm}`;
            }

            if (filterDate) {
                url += `&date=${filterDate}`;
            }

            if (filterMonth) {
                url += `&month=${filterMonth}`;
            }

            if (filterRoute) {
                url += `&route_id=${filterRoute}`;
            }

            if (filterShift) {
                url += `&shift=${filterShift}`;
            }

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setAggregations(data.data || []);
                setTotalPages(data.last_page || 1);
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                throw new Error("Failed to fetch route aggregations");
            }
        } catch (error) {
            setError("An error occurred while fetching route aggregations.");
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchTerm, filterDate, filterMonth, filterRoute, filterShift, navigate, token]);

    // Fetch negative variance totals
    const fetchNegativeVariance = useCallback(async () => {
        try {
            setTotalsLoading(true);
            let url = `${API_URL}/route-aggregations/totals`;
            const params = [];

            if (searchTerm) {
                params.push(`search=${searchTerm}`);
            }

            if (filterDate) {
                params.push(`date=${filterDate}`);
            }

            if (filterMonth) {
                params.push(`month=${filterMonth}`);
            }

            if (filterRoute) {
                params.push(`route_id=${filterRoute}`);
            }

            if (filterShift) {
                params.push(`shift=${filterShift}`);
            }

            if (params.length > 0) {
                url += `?${params.join('&')}`;
            }

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data) {
                    setNegativeVariance({
                        total: data.data.total_negative_variance || 0,
                        count: data.data.negative_variance_count || 0,
                        total_records: data.data.total_records || 0
                    });
                }
            }
        } catch (error) {
            console.error("Error fetching variance totals:", error);
        } finally {
            setTotalsLoading(false);
        }
    }, [searchTerm, filterDate, filterMonth, filterRoute, filterShift, token]);

    // Fetch totals when filters change
    useEffect(() => {
        if (isAdmin) {
            fetchNegativeVariance();
        }
    }, [filterDate, filterMonth, filterRoute, filterShift, searchTerm, isAdmin]);

    // Fetch routes on component mount
    useEffect(() => {
        fetchRoutes();
    }, [fetchRoutes]);

    // Fetch aggregations when dependencies change
    useEffect(() => {
        fetchAggregations();
    }, [fetchAggregations]);

    const toggleDropdown = (index) => {
        if (isDropdownOpen === index) {
            setIsDropdownOpen(null);
        } else {
            setIsDropdownOpen(index);
        }
    };

    const handleDeleteClick = (aggregation) => {
        setSelectedAggregation(aggregation);
        setIsConfirmDialogOpen(true);
    };

    const confirmDeleteAggregation = async () => {
        if (selectedAggregation) {
            try {
                const response = await fetch(`${API_URL}/route-aggregations/${selectedAggregation.id}`, {
                    method: 'DELETE',
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                });

                if (response.ok) {
                    setNotification({ type: "success", message: 'Route aggregation deleted successfully' });
                    fetchAggregations();
                    if (isAdmin) {
                        fetchNegativeVariance();
                    }
                } else if (response.status === 401) {
                    navigate("/logout");
                } else if (response.status === 404) {
                    setNotification({ type: "danger", message: 'Route aggregation not found.' });
                } else {
                    const errorData = await response.json();
                    setNotification({ type: "danger", message: errorData.message || 'Failed to delete route aggregation.' });
                }
            } catch (error) {
                setNotification({ message: 'An error occurred while deleting the route aggregation.', type: 'danger' });
                console.error("Error:", error);
            } finally {
                setIsConfirmDialogOpen(false);
                setSelectedAggregation(null);
            }
        }
    };

    const cancelDeleteAggregation = () => {
        setIsConfirmDialogOpen(false);
        setSelectedAggregation(null);
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    };

    const handleFilterDateChange = (event) => {
        const date = event.target.value;
        setFilterDate(date);
        setFilterMonth("");
        setCurrentPage(1);
    };

    const handleFilterMonthChange = (event) => {
        setFilterMonth(event.target.value);
        setFilterDate("");
        setCurrentPage(1);
    };

    const handleFilterRouteChange = (event) => {
        setFilterRoute(event.target.value);
        setCurrentPage(1);
    };

    const handleFilterShiftChange = (event) => {
        setFilterShift(event.target.value);
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setSearchTerm("");
        setFilterDate("");
        setFilterMonth("");
        setFilterRoute("");
        setFilterShift("");
        setCurrentPage(1);
    };

    // Handle click outside dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isDropdownOpen !== null) {
                const dropdown = document.querySelector(`.dropdown.is-active`);
                if (dropdown && !dropdown.contains(event.target)) {
                    setIsDropdownOpen(null);
                }
            }
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [isDropdownOpen]);

    return (
        <div>
            <main>
                <nav className="level">
                    <div className="level-left">
                        <div className="level-item">
                            <span className="title">Route Aggregations</span>
                        </div>
                    </div>

                    {isAdmin && (
                        <div className="level-item has-text-centered">
                            {totalsLoading ? (
                                <div className="has-text-grey">
                                    <span className="icon">
                                        <i className="fas fa-spinner fa-spin"></i>
                                    </span>
                                    <span>Calculating variance...</span>
                                </div>
                            ) : (
                                <div className="">
                                    <div className="content has-text-centered">
                                        <p className="heading">Total Negative Variance:  {negativeVariance.total.toFixed(2)} L</p>
                                      
                                        <p className="subtitle is-6">
                                            ({negativeVariance.count} out of {negativeVariance.total_records} records)
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="level-right">
                        <p className="level-item">
                            <Link to="add" className="button primary-bg">
                                <i className="fa fa-plus" aria-hidden="true"></i> &nbsp; New Aggregation
                            </Link>
                        </p>
                    </div>
                </nav>

                {notification.message && (
                    <Notification
                        type={notification.type}
                        message={notification.message}
                        onClose={closeNotification}
                    />
                )}

                {/* Filters */}
                <div className="field is-horizontal">
                    <div className="field-body">
                        <div className="field">
                            <label className="label">Search</label>
                            <div className="control">
                                <input
                                    className="input"
                                    type="text"
                                    placeholder="Search by route or aggregator"
                                    value={searchTerm}
                                    onChange={handleSearch}
                                />
                            </div>
                        </div>
                        <div className="field">
                            <label className="label">Filter by Route</label>
                            <div className="control">
                                <div className="select is-fullwidth">
                                    <select
                                        value={filterRoute}
                                        onChange={handleFilterRouteChange}
                                    >
                                        <option value="">All Routes</option>
                                        {routes.map((route) => (
                                            <option key={route.id} value={route.id}>
                                                {route.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="field">
                            <label className="label">Filter by Shift</label>
                            <div className="control">
                                <div className="select is-fullwidth">
                                    <select
                                        value={filterShift}
                                        onChange={handleFilterShiftChange}
                                    >
                                        {shifts.map((shift) => (
                                            <option key={shift.value} value={shift.value}>
                                                {shift.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="field">
                            <label className="label">Filter by Date</label>
                            <div className="control">
                                <input
                                    type="date"
                                    className="input"
                                    value={filterDate}
                                    onChange={handleFilterDateChange}
                                />
                            </div>
                        </div>
                        <div className="field">
                            <label className="label">Filter by Month</label>
                            <div className="control">
                                <input
                                    type="month"
                                    className="input"
                                    value={filterMonth}
                                    onChange={handleFilterMonthChange}
                                />
                            </div>
                        </div>

                        <div className="field">
                            <label className="label">.</label>
                            <div className="control">
                                <button
                                    className="button is-light"
                                    onClick={clearFilters}
                                    disabled={!searchTerm && !filterDate && !filterMonth && !filterRoute && !filterShift}
                                >
                                    Clear Filters
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <p>Loading route aggregations...</p>
                ) : error ? (
                    <p className="has-text-danger">{error}</p>
                ) : (
                    <>
                        <table className="table is-striped is-fullwidth">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Route</th>
                                    <th>Date & Shift</th>
                                    <th>Aggregated</th>

                                    {isAdmin && (
                                        <>
                                            <th>Field Total</th>
                                            <th>Variance</th>
                                        </>
                                    )}

                                    <th>Sold</th>
                                    <th>Sent to Processor</th>
                                    <th>Notes</th>
                                    <th>Posted At</th>
                                    <th>Posted By</th>
                                    <th>Options</th>
                                </tr>
                            </thead>
                            <tbody>
                                {aggregations.length > 0 ? (
                                    aggregations.map((aggregation, index) => {
                                        const soldLitres = aggregation.sold_litres || 0;
                                        const remainingForProcessor = aggregation.remaining_for_processor || 0;
                                        const fieldTotal = aggregation.field_total_litres || 0;
                                        const variance = aggregation.variance || 0;

                                        return (
                                            <tr key={aggregation.id}>
                                                <td>{(currentPage - 1) * perPage + index + 1}</td>
                                                <td>{aggregation.route?.name || 'N/A'}</td>
                                                <td>
                                                    {formatToDayMonthYear(aggregation.collection_date)}<br />
                                                    <small className="tag is-light">{aggregation.shift || 'N/A'}</small>
                                                </td>
                                                <td className="has-text-weight-bold">
                                                    {parseFloat(aggregation.litres).toFixed(2)}
                                                </td>
                                                {isAdmin && (
                                                    <>
                                                        <td>
                                                            {fieldTotal.toFixed(2)}
                                                        </td>
                                                        <td>
                                                            <span className={`tag ${variance < 0 ? 'is-danger' : variance > 0 ? 'is-success' : 'is-light'}`}>
                                                                {variance > 0 ? '+' : ''}{variance.toFixed(2)}
                                                            </span>
                                                        </td>
                                                    </>
                                                )}

                                                <td>
                                                    {soldLitres > 0 ? (
                                                        <span>
                                                            {soldLitres.toFixed(2)} L
                                                        </span>
                                                    ) : (
                                                        <span>Not sold</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {remainingForProcessor.toFixed(2)}
                                                </td>
                                                <td title={aggregation.notes}>
                                                    {aggregation.notes ?
                                                        (aggregation.notes.length > 20 ?
                                                            `${aggregation.notes.substring(0, 20)}...` :
                                                            aggregation.notes)
                                                        : '-'}
                                                </td>
                                                <td>{(formatTimestamp(aggregation.created_at) || " ")}</td>
                                                <td>{aggregation.aggregator?.first_name} {aggregation.aggregator?.last_name}</td>
                                                <td>
                                                    <div className={`dropdown is-right ${isDropdownOpen === index ? 'is-active' : ''}`}>
                                                        <div className="dropdown-trigger">
                                                            <button
                                                                className="button is-white"
                                                                aria-haspopup="true"
                                                                aria-controls="dropdown-menu"
                                                                onClick={() => toggleDropdown(index)}
                                                            >
                                                                <span className="icon">
                                                                    <i className="fas fa-ellipsis-v"></i>
                                                                </span>
                                                            </button>
                                                        </div>
                                                        <div className="dropdown-menu" id="dropdown-menu" role="menu">
                                                            <div className="dropdown-content">
                                                                {canEdit(userRole) && (
                                                                    <>
                                                                        <Link to={`${aggregation.id}/update`} className="dropdown-item">
                                                                            <span className="icon">
                                                                                <i className="fas fa-edit"></i>
                                                                            </span>
                                                                            <span>Edit</span>
                                                                        </Link>
                                                                        <Link
                                                                            className="dropdown-item"
                                                                            onClick={() => handleDeleteClick(aggregation)}
                                                                        >
                                                                            <span className="icon">
                                                                                <i className="fas fa-trash-alt"></i>
                                                                            </span>
                                                                            <span>Delete</span>
                                                                        </Link>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="14" className="has-text-centered">
                                            No route aggregations found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {/* Pagination Controls */}
                        <nav className="pagination is-centered">
                            <button
                                className="pagination-previous"
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            <span className="pagination-page">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                className="pagination-next"
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </nav>
                    </>
                )}

                {/* Confirmation Dialog */}
                {isConfirmDialogOpen && (
                    <div className="modal is-active">
                        <div className="modal-background" onClick={cancelDeleteAggregation}></div>
                        <div className="modal-card">
                            <header className="modal-card-head">
                                <p className="modal-card-title">Confirm Delete</p>
                                <button className="delete" aria-label="close" onClick={cancelDeleteAggregation}></button>
                            </header>
                            <section className="modal-card-body">
                                <p>Are you sure you want to delete the aggregation for route
                                    "<strong>{selectedAggregation?.route?.name}</strong>" on
                                    {formatToDayMonthYear(selectedAggregation?.collection_date)} ({selectedAggregation?.shift})?
                                </p>
                                <p className="has-text-danger">This action cannot be undone.</p>
                            </section>
                            <footer className="modal-card-foot">
                                <button className="button is-danger" onClick={confirmDeleteAggregation}>
                                    Delete
                                </button>
                                <button className="button" onClick={cancelDeleteAggregation}>
                                    Cancel
                                </button>
                            </footer>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}

export default Aggregations;